import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', slate: '#5C6B7A', bg: '#F4F6F9' };
const BAND = {
  fresh: { label: 'Fresh', color: C.green, hint: 'worked inside 30 days' },
  warming: { label: 'Warming', color: C.amber, hint: '30–59 days idle' },
  chase_due: { label: 'Chase due', color: C.red, hint: '60+ days — ARIA will chase' },
  escalated: { label: 'Escalated', color: C.red, hint: 'with the MGA admin' },
};
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '—');

function Band({ k, n, active, onClick }) {
  const b = BAND[k];
  return (
    <button onClick={onClick} className="rounded-sm px-3 py-2 text-left" style={{ background: active ? b.color + '18' : C.bg, outline: active ? `1px solid ${b.color}` : 'none' }} data-testid={`queue-band-${k}`}>
      <p className="text-[10px] uppercase tracking-wide" style={{ color: C.slate }}>{b.label}</p>
      <p className="text-sm font-semibold" style={{ color: n ? b.color : C.navy }}>{n}</p>
      <p className="text-[9px]" style={{ color: C.slate }}>{b.hint}</p>
    </button>
  );
}

function WorkIt({ claim, api, onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 6) { toast.error('Say what you did — at least a few words'); return; }
    setBusy(true);
    try {
      await api.post(`/claims/${claim.claim_id}/note`, { text: text.trim(), note_type: 'action' });
      toast.success(`Worked ${claim.claim_reference || claim.claim_number} — idle clock reset`);
      setText('');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the action'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="what you did…" className="text-[11px] border rounded-sm px-1.5 py-1 bg-white w-44" data-testid={`queue-work-input-${claim.claim_id}`} />
      <button onClick={send} disabled={busy} className="p-1 rounded-sm text-white disabled:opacity-50" style={{ background: C.blue }} title="Record the action — resets the idle clock" data-testid={`queue-work-btn-${claim.claim_id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
      </button>
    </div>
  );
}

export default function HandlerQueuePage() {
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [handler, setHandler] = useState('');
  const [band, setBand] = useState(null);

  const load = useCallback(() => api.get('/claims/my-queue', { params: handler ? { handler } : {} }).then(r => setData(r.data)).catch(() => setData({ claims: [], roster: [], bands: {}, handler: null })), [api, handler]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 text-sm" style={{ color: C.slate }} data-testid="queue-loading">Loading your queue…</div>;
  const rows = (data.claims || []).filter(c => !band || c.band === band);
  const isStaff = ['mga_admin', 'platform_owner', 'underwriter'].includes(user?.role);

  return (
    <div className="space-y-4" data-testid="handler-queue-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold" style={{ color: C.navy }}>
            {data.handler ? `${data.handler.name}'s queue — ${data.count} open file(s)` : 'Handler queue'}
          </h2>
          <p className="text-[11px] mt-1 max-w-3xl" style={{ color: C.slate }}>{data.basis}</p>
        </div>
        {(isStaff || !data.handler) && (
          <select value={handler} onChange={e => setHandler(e.target.value)} className="text-[11px] border rounded-sm px-2 py-1 bg-white" data-testid="queue-handler-select">
            <option value="">{data.handler ? 'my own queue' : 'pick a handler…'}</option>
            {(data.roster || []).map(h => <option key={h.id} value={h.id}>{h.name} · {h.open_files} open</option>)}
          </select>
        )}
      </div>

      {data.handler && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.keys(BAND).map(k => <Band key={k} k={k} n={data.bands?.[k] || 0} active={band === k} onClick={() => setBand(band === k ? null : k)} />)}
          <div className="rounded-sm px-3 py-2" style={{ background: C.bg }} data-testid="queue-overdue-diaries">
            <p className="text-[10px] uppercase tracking-wide" style={{ color: C.slate }}>Overdue diaries</p>
            <p className="text-sm font-semibold" style={{ color: data.overdue_diaries ? C.amber : C.navy }}>{data.overdue_diaries || 0}</p>
            <p className="text-[9px]" style={{ color: C.slate }}>files with a diary point past due</p>
          </div>
        </div>
      )}

      {data.handler && rows.length === 0 && (
        <div className="flex items-center gap-2 p-6 rounded-sm" style={{ background: C.bg }} data-testid="queue-empty">
          <CheckCircle2 className="w-4 h-4" style={{ color: C.green }} />
          <span className="text-sm" style={{ color: C.navy }}>{band ? `Nothing in "${BAND[band].label}".` : 'No open files — the queue is clear.'}</span>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto bg-white border rounded-sm" style={{ borderColor: 'rgba(136,150,168,0.3)' }}>
          <table className="w-full text-[11px]" data-testid="queue-table">
            <thead><tr style={{ color: C.slate, background: C.bg }}>
              <th className="text-left px-3 py-2">claim</th><th className="text-left">insured · status</th><th className="text-right">idle</th>
              <th className="text-left pl-3">ARIA</th><th className="text-right">reserve</th><th className="text-left pl-3">next diary</th><th className="text-left pl-3">last worked</th><th className="text-left pl-3 w-56">work it</th>
            </tr></thead>
            <tbody>
              {rows.map(c => {
                const b = BAND[c.band];
                return (
                  <tr key={c.claim_id} onClick={() => navigate(`/claims?tab=active&claim=${c.claim_id}`)} className="border-t align-top cursor-pointer hover:bg-[#FAFBFC]" style={{ borderColor: 'rgba(136,150,168,0.15)', boxShadow: `inset 3px 0 0 ${b.color}` }} data-testid={`queue-row-${c.claim_id}`}>
                    <td className="px-3 py-2 font-mono whitespace-nowrap" style={{ color: C.blue }}>{c.claim_reference || c.claim_number}<div className="text-[10px]" style={{ color: C.slate }}>{c.days_open}d open{c.sanctions_hold ? ' · SANCTIONS HOLD' : ''}</div></td>
                    <td className="py-2" style={{ color: C.navy }}><div className="truncate max-w-[200px]">{c.business_name}</div><div className="text-[10px] capitalize" style={{ color: C.slate }}>{(c.status || '').replace(/_/g, ' ')} · {c.claim_type || '—'}</div></td>
                    <td className="py-2 text-right font-semibold whitespace-nowrap" style={{ color: b.color }} data-testid={`queue-idle-${c.claim_id}`}>{c.idle_days}d</td>
                    <td className="py-2 pl-3 whitespace-nowrap" style={{ color: C.slate }}>
                      {c.band === 'escalated' ? <span style={{ color: C.red }}>escalated · with MGA admin</span>
                        : c.band === 'chase_due' ? <span style={{ color: C.red }}>{c.chases ? `chased ×${c.chases}` : 'chase due now'} · escalation in {c.days_to_escalation}d</span>
                        : <span>chase in <strong style={{ color: C.navy }}>{c.days_to_chase}d</strong></span>}
                    </td>
                    <td className="py-2 text-right" style={{ color: C.navy }}>{money(c.reserve_amount)}</td>
                    <td className="py-2 pl-3 max-w-[200px]" style={{ color: c.next_diary?.overdue ? C.amber : C.slate }}>{c.next_diary ? <span title={c.next_diary.action}>{c.next_diary.overdue ? 'OVERDUE · ' : ''}{(c.next_diary.due_date || '').slice(0, 10)} · {c.next_diary.action}</span> : '—'}</td>
                    <td className="py-2 pl-3 max-w-[220px]" style={{ color: C.slate }}>{c.last_human_note ? <span title={c.last_human_note.text}>{c.last_human_note.at?.slice(0, 10)} · {c.last_human_note.by}</span> : <em>nothing since FNOL</em>}</td>
                    <td className="py-2 pl-3"><WorkIt claim={c} api={api} onDone={load} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] flex items-center gap-1" style={{ color: C.slate }}><Clock className="w-3 h-3" /> Click a row to open the file; type what you did and press Enter to record it without leaving the queue.</p>
    </div>
  );
}
